import { useEffect, useState } from "react";
import { Building2, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { useAuth } from "../lib/auth";
import { fetchBusinessInfo, updateBusinessInfo } from "../lib/api";
import { fetchBranding } from "../lib/company";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const TIMEZONES = [
  ["Pacific/Auckland", "New Zealand (Auckland)"],
  ["Pacific/Chatham", "New Zealand (Chatham Islands)"],
  ["Australia/Sydney", "NSW / ACT (Sydney)"],
  ["Australia/Melbourne", "Victoria (Melbourne)"],
  ["Australia/Brisbane", "Queensland (Brisbane, no DST)"],
  ["Australia/Adelaide", "South Australia (Adelaide)"],
  ["Australia/Darwin", "Northern Territory (Darwin)"],
  ["Australia/Hobart", "Tasmania (Hobart)"],
  ["Australia/Perth", "Western Australia (Perth)"],
];

/**
 * Business letterhead — appears on every owner-notice letter (and is
 * snapshotted into each saved letter so old letters remain printable
 * verbatim even after you change details here).
 */
export default function BusinessInfoCard() {
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedTz, setSavedTz] = useState(null);

  useEffect(() => {
    fetchBusinessInfo()
      .then((c) => { setCfg(c); setSavedTz(c.timezone); })
      .catch(() => toast.error("Failed to load business info"));
  }, []);

  const upd = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const next = await updateBusinessInfo({
        trading_name: cfg.trading_name,
        trading_as: cfg.trading_as,
        address_line1: cfg.address_line1,
        address_line2: cfg.address_line2,
        phone_primary: cfg.phone_primary,
        phone_secondary: cfg.phone_secondary,
        email: cfg.email,
        gst_number: cfg.gst_number,
        sanity_radius_km: cfg.sanity_radius_km ? parseInt(cfg.sanity_radius_km, 10) : undefined,
        timezone: cfg.timezone,
      });
      const tzChanged = next.timezone !== savedTz;
      setCfg(next);
      setSavedTz(next.timezone);
      toast.success("Business info saved");
      if (tzChanged) {
        // Every on-screen time depends on the tenant timezone — reload so
        // the whole app re-renders in the new zone.
        await fetchBranding({ force: true });
        toast.info("Timezone changed — reloading…");
        setTimeout(() => window.location.reload(), 800);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return (
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6">
        <p className="text-sm text-slate-500">Loading business info…</p>
      </section>
    );
  }

  return (
    <section
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6 space-y-4"
      data-testid="business-info-card"
    >
      <div className="flex items-center gap-2">
        <Building2 className="h-5 w-5 text-slate-700 dark:text-slate-300" />
        <h2 className="font-display text-lg font-black text-slate-800 dark:text-slate-100">
          Business Letterhead
        </h2>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2">
        Appears on every owner-notice letter and any other letterhead document the app prints. Each saved letter snapshots these values, so older letters reprint exactly as they were originally generated even if you change details here.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="uppercase-label">Trading name</Label>
          <Input value={cfg.trading_name || ""} onChange={(e) => upd("trading_name", e.target.value)} disabled={!isAdmin} data-testid="bi-trading-name" />
        </div>
        <div>
          <Label className="uppercase-label">Trading as</Label>
          <Input value={cfg.trading_as || ""} onChange={(e) => upd("trading_as", e.target.value)} disabled={!isAdmin} data-testid="bi-trading-as" />
        </div>
        <div>
          <Label className="uppercase-label">Address line 1</Label>
          <Input value={cfg.address_line1 || ""} onChange={(e) => upd("address_line1", e.target.value)} disabled={!isAdmin} data-testid="bi-address-1" />
        </div>
        <div>
          <Label className="uppercase-label">Address line 2 (suburb / city)</Label>
          <Input value={cfg.address_line2 || ""} onChange={(e) => upd("address_line2", e.target.value)} disabled={!isAdmin} data-testid="bi-address-2" />
        </div>
        <div>
          <Label className="uppercase-label">Phone (mobile)</Label>
          <Input value={cfg.phone_primary || ""} onChange={(e) => upd("phone_primary", e.target.value)} disabled={!isAdmin} data-testid="bi-phone-1" />
        </div>
        <div>
          <Label className="uppercase-label">Phone (landline)</Label>
          <Input value={cfg.phone_secondary || ""} onChange={(e) => upd("phone_secondary", e.target.value)} disabled={!isAdmin} data-testid="bi-phone-2" />
        </div>
        <div>
          <Label className="uppercase-label">Email</Label>
          <Input type="email" value={cfg.email || ""} onChange={(e) => upd("email", e.target.value)} disabled={!isAdmin} data-testid="bi-email" />
        </div>
        <div>
          <Label className="uppercase-label">GST number</Label>
          <Input value={cfg.gst_number || ""} onChange={(e) => upd("gst_number", e.target.value)} disabled={!isAdmin} data-testid="bi-gst-number" />
        </div>
        <div>
          <Label className="uppercase-label">Service radius (km)</Label>
          <Input
            type="number" min="50" max="2000" step="10"
            value={cfg.sanity_radius_km ?? 300}
            onChange={(e) => upd("sanity_radius_km", e.target.value)}
            disabled={!isAdmin}
            data-testid="bi-sanity-radius"
          />
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
            Round-trip km &gt; 4× this value shows a "please review" warning on the job.
            Metro fleets ~100 km; regional ~300 km; long-haul ~500 km+.
          </p>
        </div>
        <div>
          <Label className="uppercase-label">Company timezone</Label>
          <Select
            value={cfg.timezone || "Pacific/Auckland"}
            onValueChange={(v) => upd("timezone", v)}
            disabled={!isAdmin}
          >
            <SelectTrigger data-testid="bi-timezone"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEZONES.map(([v, label]) => (
                <SelectItem key={v} value={v} data-testid={`bi-timezone-opt-${v.replace("/", "-").toLowerCase()}`}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
            Drives "today", after-hours, storage days, booking times and document dates — for every user, whatever their device clock says.
          </p>
        </div>
      </div>

      {isAdmin && (
        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="bi-save">
            <Save className="h-4 w-4.5" />
            {saving ? "Saving…" : "Save Letterhead"}
          </Button>
        </div>
      )}
    </section>
  );
}
